import { useQuery } from '@tanstack/react-query'
import { TenantConfigPlane } from '@today/types'
import { type TenantConfigManifest, tenantConfigService } from '@/services/tenant-config.service'

/** Why the manifest could not be shown: the page renders a different screen for each. */
export enum TenantConfigLoadFailure {
	FORBIDDEN = 'FORBIDDEN',
	NOT_FOUND = 'NOT_FOUND',
	ERROR = 'ERROR',
}

const failureOf = (error: unknown): TenantConfigLoadFailure => {
	const status = (error as { response?: { status?: unknown } } | null)?.response?.status
	if (status === 403) return TenantConfigLoadFailure.FORBIDDEN
	if (status === 404) return TenantConfigLoadFailure.NOT_FOUND
	return TenantConfigLoadFailure.ERROR
}

/**
 * The console's manifest for one tenant on one plane (TO-374). `target` is the
 * tenant id on the platform plane and the slug on the tenant plane; the server
 * decides whether the caller may address it.
 */
export function useTenantConfigManifest(plane: TenantConfigPlane, target: string) {
	const query = useQuery<TenantConfigManifest>({
		queryKey: ['tenant-config-manifest', plane, target],
		queryFn: () =>
			plane === TenantConfigPlane.PLATFORM
				? tenantConfigService.getPlatform(target)
				: tenantConfigService.getTenant(target),
	})
	return {
		manifest: query.data ?? null,
		isLoading: query.isLoading,
		failure: query.isError ? failureOf(query.error) : null,
		retry: () => void query.refetch(),
	}
}
